/**
 * Legacy `IM_MAX_SCALE_RATE` (`page-editor`'s `odctrl.js`) — the max points-per-pixel a photo
 * can be cover-fit to before dropping below a 150 DPI print floor. Applied to a photo's natural
 * pixel dimensions, it yields the largest pt size that still holds ≥150 DPI.
 */
const MAX_FILL_SCALE_RATIO = 72 / 150

/**
 * Returns true when the image is large enough to fill the shape without
 * dropping below a 150 DPI print floor.
 *
 * The warning fires only when cover-fitting the photo would require scaling
 * it larger than its 150-DPI size — i.e. when the frame is bigger than the
 * photo's `MAX_FILL_SCALE_RATIO`-scaled size in at least one dimension. If the
 * photo can cover the frame by scaling DOWN, no warning is shown regardless of
 * print DPI, because no stretching occurs.
 *
 * High-resolution images (e.g. ≥300 DPI relative to the frame) always pass
 * this check since their pixel count far exceeds the frame's point size.
 */
export function meetsMinimumResolution(
  naturalWidth: number,
  naturalHeight: number,
  shapeWidth: number,
  shapeHeight: number
): boolean {
  if (shapeWidth <= 0 || shapeHeight <= 0) return true

  return (
    naturalWidth * MAX_FILL_SCALE_RATIO >= shapeWidth &&
    naturalHeight * MAX_FILL_SCALE_RATIO >= shapeHeight
  )
}

/**
 * Largest a photo-filled shape can be sized (pt) before it would drop below a 150 DPI print
 * floor — the same `MAX_FILL_SCALE_RATIO` convention `meetsMinimumResolution` checks, exposed as
 * a ceiling instead of a boolean. Used to silently cap resize (drag or typed) rather than warn.
 * Returns null when natural size isn't known yet (nothing to cap against).
 */
export function getMaxPhotoFillSize(
  naturalWidth: number | null | undefined,
  naturalHeight: number | null | undefined
): { maxWidth: number; maxHeight: number } | null {
  if (naturalWidth == null || naturalHeight == null) return null

  return {
    maxWidth: naturalWidth * MAX_FILL_SCALE_RATIO,
    maxHeight: naturalHeight * MAX_FILL_SCALE_RATIO,
  }
}

/**
 * The photo's current *rendered* (covered) size in pt, given a frame size — i.e. what
 * `preserveAspectRatio="xMidYMid slice"` would cover the frame with right now, times our own
 * extra `scale` zoom. This is legacy `page-editor`'s frozen `content.scaledWidth`/`scaledHeight`
 * equivalent (`odeditorsvg.js` `ODImage`), captured at a point in time rather than recomputed
 * every render — callers use it to *freeze* the effective size across a crop-only resize (see
 * `computePhotoFillOffsetAfterResize`) instead of letting the browser silently re-cover the new
 * frame shape, and to cap crop growth so the frame can never exceed what the (now frozen) photo
 * actually covers. Returns null when natural size isn't known yet.
 */
export function getCurrentPhotoFillCoveredSize(
  frameWidth: number,
  frameHeight: number,
  naturalWidth: number | null | undefined,
  naturalHeight: number | null | undefined,
  scale: number
): { coveredWidth: number; coveredHeight: number } | null {
  if (naturalWidth == null || naturalHeight == null) return null
  if (naturalWidth <= 0 || naturalHeight <= 0) return null
  if (!(frameWidth > 0) || !(frameHeight > 0)) return null

  const coverScale = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight) * scale

  return {
    coveredWidth: naturalWidth * coverScale,
    coveredHeight: naturalHeight * coverScale,
  }
}

/**
 * Largest `ShapePhotoFill.scale` a photo can be zoomed to, for a *fixed* frame size, before its
 * effective resolution drops below the same 150 DPI floor `getMaxPhotoFillSize` enforces for a
 * fixed-scale frame resize — the crop-mode zoom slider's complementary constraint (frame fixed,
 * scale varies, vs. frame varies at scale 1).
 *
 * At `scale` 1, the native cover-fit binding axis (`Math.max(frameWidth/naturalWidth,
 * frameHeight/naturalHeight)`, matching `getCurrentPhotoFillCoveredSize`) already renders at
 * `72 / nativeCoverScale` DPI. Zooming to `scale` spreads the same natural pixels over a
 * proportionally larger rendered area, so the effective DPI shown in the frame is that value
 * divided by `scale` — solving for the `scale` where it hits the 150 DPI floor gives
 * `(72 / nativeCoverScale) / 150`, which simplifies to `MAX_FILL_SCALE_RATIO / nativeCoverScale`.
 *
 * Returns null when natural size isn't known yet (nothing to cap against) or the frame is
 * empty. Floored at 1 (never below native cover, matching `computePhotoFillOffsetAfterResize`'s
 * own scale floor) — a photo whose natural resolution can't even meet 150 DPI at scale 1 for
 * this frame has no headroom to zoom into at all.
 */
export function getMaxPhotoFillZoomScale(
  frameWidth: number,
  frameHeight: number,
  naturalWidth: number | null | undefined,
  naturalHeight: number | null | undefined
): number | null {
  if (naturalWidth == null || naturalHeight == null) return null
  if (naturalWidth <= 0 || naturalHeight <= 0) return null
  if (!(frameWidth > 0) || !(frameHeight > 0)) return null

  const nativeCoverScale = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight)

  return Math.max(1, MAX_FILL_SCALE_RATIO / nativeCoverScale)
}

/**
 * Resolves the natural size to use for cover-fit math when the shape's true
 * `naturalWidth`/`naturalHeight` haven't been backfilled yet (checkpoint load never carries them
 * on the wire — see `checkpoint-to-shapes.ts` — and the async re-measure effect in
 * `center-canvas/hooks/use-photo-fill-natural-size.ts` can lose the race if a resize starts moments after a spread loads, or never
 * fires at all for a photo whose preview URL wasn't resolved yet when that effect ran).
 *
 * Falls back to `persistedRenderedWidth`/`persistedRenderedHeight` — the rendered (covered) size
 * as of the last save. Its absolute values are stale by definition (they reflect whatever crop/
 * zoom was in effect back then), but its *ratio* is exactly the photo's natural aspect ratio
 * (scale and cover-fit both cancel out of a width:height ratio), which is all `scale`-freeze and
 * slack-clamp math actually depends on — `getCurrentPhotoFillCoveredSize`'s
 * `max(frameWidth/naturalWidth, frameHeight/naturalHeight)` and the offset re-anchor derived from
 * it are provably invariant to substituting any same-ratio stand-in for the true natural size.
 * Returns `{ naturalWidth: undefined, naturalHeight: undefined }` when neither is available —
 * callers already treat that as "can't clamp/freeze yet" (skip gracefully, matching today's
 * behavior when natural size is unknown).
 */
export function resolveEffectivePhotoFillNaturalSize(params: {
  naturalWidth?: number | null
  naturalHeight?: number | null
  persistedRenderedWidth?: number | null
  persistedRenderedHeight?: number | null
}): { naturalWidth?: number | null; naturalHeight?: number | null } {
  const { naturalWidth, naturalHeight, persistedRenderedWidth, persistedRenderedHeight } = params

  if (naturalWidth != null && naturalHeight != null) {
    return { naturalWidth, naturalHeight }
  }

  return { naturalWidth: persistedRenderedWidth, naturalHeight: persistedRenderedHeight }
}
